import axios from 'axios';
import type { SurveyData } from '@/lib/index';

// ─────────────────────────────────────────────────────────────────────────────
// Web3Forms config
// To activate: go to https://web3forms.com, enter mattiesmith46@mappedbymattie.com
// and paste the access key you receive into the variable below.
// ─────────────────────────────────────────────────────────────────────────────
const WEB3FORMS_ACCESS_KEY = import.meta.env.VITE_WEB3FORMS_KEY as string || 'YOUR_WEB3FORMS_ACCESS_KEY';
const RECIPIENT_EMAIL = 'mattiesmith46@mappedbymattie.com';

// ── Helpers ───────────────────────────────────────────────────────────────────

function row(label: string, value: string | string[] | undefined): string {
  if (!value || (Array.isArray(value) && value.length === 0) || value === '') return '';
  const display = Array.isArray(value) ? value.join(', ') : value;
  return `<tr>
    <td style="padding:8px 12px;font-weight:600;color:#4A5A40;background:#EEF2EA;width:220px;vertical-align:top;font-size:13px;border-bottom:1px solid #E0D8DC;white-space:nowrap">${label}</td>
    <td style="padding:8px 12px;color:#1E1A1C;background:#ffffff;font-size:13px;border-bottom:1px solid #E0D8DC">${display}</td>
  </tr>`;
}

function section(title: string, rows: string): string {
  if (!rows.trim()) return '';
  return `<tr>
    <td colspan="2" style="padding:14px 12px 8px;background:#2E2830;color:#C9A84C;font-size:11px;font-weight:700;letter-spacing:2px;text-transform:uppercase">${title}</td>
  </tr>
  ${rows}`;
}

function buildEmailHtml(d: SurveyData): string {
  const submittedAt = new Date().toLocaleString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
  });

  const contactRows = [
    row('Name', d.name),
    row('Email', d.email),
    row('Instagram', d.instagram),
    row('Referral', d.referral),
  ].join('');

  const groupRows = [
    row('Group Size', d.groupSize),
    row('Age Range(s)', d.ageRange),
    row('Group Relationship', d.groupRelationship),
    row('Occasion', d.occasion),
    row('Travel Experience', d.travelExperience),
  ].join('');

  const logisticsRows = [
    row('Preferred Dates', d.preferredDates),
    row('Date Flexibility', d.flexibility),
    row('Trip Duration', d.duration),
    row('Has Destination?', d.hasDestination),
    row('Destination Ideas', d.destinationIdeas),
    row('International OK?', d.internationalOk),
    row('Destinations to Avoid', d.avoidDestinations),
  ].join('');

  const budgetRows = [
    row('Budget Per Person', d.budgetPerPerson),
    row('Budget Includes', d.budgetIncludes),
    row('Group Fund?', d.groupFund),
  ].join('');

  const accommodationRows = [
    row('Accommodation Type', d.accommodationType),
    row('Room Sharing', d.roomSharing),
    row('Amenities', d.amenities),
  ].join('');

  const vibeRows = [
    row('Trip Type', d.tripType),
    row('Overall Vibe', d.overallVibe),
    row('Top Priorities', d.topPriorities),
  ].join('');

  const partyRows = [
    row('Nightlife Types', d.nightlifeTypes),
    row('Night Out Budget', d.nightOutBudget),
    row('Drinking Status', d.drinkingStatus),
    row('VIP Preferences', d.vipPreferences),
    row('Night Out Frequency', d.nightOutFrequency),
    row('Day Parties', d.dayParties),
    row('Specific Events', d.specificPartyEvents),
    row('Dress Code', d.dressCode),
  ].join('');

  const wellnessRows = [
    row('Wellness Activities', d.wellnessActivities),
    row('Wellness Intention', d.wellnessIntention),
    row('Nutrition Focus', d.nutritionFocus),
    row('Activity Level', d.activityLevel),
    row('Health Notes', d.healthNotes),
    row('Wellness Setting', d.wellnessSetting),
  ].join('');

  const adventureRows = [
    row('Adventure Activities', d.adventureActivities),
    row('Adventure Level', d.adventureLevel),
    row('Limitations', d.adventureLimitations),
    row('Nature Setting', d.natureSetting),
    row('Guide Preference', d.guidePreference),
    row('Adventure Days', d.adventureDays),
    row('Downtime Preference', d.adventureDowntime),
  ].join('');

  const beachRows = [
    row('Beach Activities', d.beachActivities),
    row('Beach Environment', d.beachEnvironment),
    row('Pool vs Beach', d.poolVsBeach),
    row('Beach Club Importance', d.beachClubImportance),
    row('Spa Add-on', d.beachSpaAddOn),
  ].join('');

  const culturalRows = [
    row('Food Experiences', d.foodExperiences),
    row('Dining Boldness', d.diningBoldness),
    row('Cultural Activities', d.culturalActivities),
    row('Cultural Depth', d.culturalDepth),
    row('Cultural Interests', d.culturalInterests),
  ].join('');

  const diningRows = [
    row('Dietary Restrictions', d.dietaryRestrictions),
    row('Dietary Other', d.dietaryOther),
    row('Dining Experiences', d.diningExperiences),
    row('Meal Structure', d.mealStructure),
    row('Cuisine Preferences', d.cuisinePrefs),
    row('Dining Budget', d.diningBudget),
    row('Drink Preferences', d.drinkPrefs),
    row('Private Chef Interest', d.privateChef),
    row('Dining Notes', d.diningNotes),
  ].join('');

  const finalRows = [
    row('Transport Preference', d.transportPrefs),
    row('Airport Transfer', d.airportTransfer),
    row('Group Dynamics', d.groupDynamics),
    row('Surprise Trip?', d.isSurprise),
    row('Special Accommodations', d.specialAccommodations),
    row('Itinerary Deadline', d.itineraryDeadline),
    row('How They Found You', d.referralSource),
    row('Final Notes', d.finalNotes),
  ].join('');

  // Only include the deep dive section relevant to their trip type
  const typeSection =
    d.tripType === 'party'    ? section('Party and Nightlife Details', partyRows) :
    d.tripType === 'wellness' ? section('Wellness Details', wellnessRows) :
    d.tripType === 'adventure'? section('Adventure Details', adventureRows) :
    d.tripType === 'beach'    ? section('Beach and Relaxation Details', beachRows) :
    d.tripType === 'cultural' ? section('Cultural and Foodie Details', culturalRows) : '';

  const tableBody = [
    section('Contact Info', contactRows),
    section('Group Details', groupRows),
    section('Trip Logistics', logisticsRows),
    section('Budget', budgetRows),
    section('Accommodation', accommodationRows),
    section('Vibe and Trip Type', vibeRows),
    typeSection,
    section('Dining Preferences', diningRows),
    section('Final Details', finalRows),
  ].join('');

  return `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"/></head>
<body style="margin:0;padding:20px;background:#FAF7F5;font-family:Arial,Helvetica,sans-serif">
  <div style="max-width:700px;margin:0 auto;border-radius:8px;overflow:hidden;box-shadow:0 2px 12px rgba(46,40,48,.12)">

    <!-- Header -->
    <div style="background:#2E2830;padding:28px 24px;text-align:center">
      <h1 style="margin:0 0 4px;color:#C9A84C;font-size:24px;letter-spacing:1px">Mapped by Mattie</h1>
      <p style="margin:0;color:rgba(250,247,245,.5);font-size:11px;letter-spacing:3px;text-transform:uppercase">New Survey Submission</p>
    </div>

    <!-- Summary bar -->
    <div style="background:#4A5A40;padding:14px 24px;display:flex;align-items:center;gap:16px">
      <div>
        <p style="margin:0;font-size:15px;font-weight:700;color:#ffffff">
          ${d.name || 'A new client'} just submitted their trip survey
        </p>
        <p style="margin:4px 0 0;font-size:12px;color:rgba(255,255,255,.65)">
          ${submittedAt}
        </p>
      </div>
    </div>

    <!-- Quick snapshot -->
    <table width="100%" cellpadding="0" cellspacing="0"
           style="border-collapse:collapse;background:#F9F0F5;border-bottom:2px solid #B07FA0">
      <tr>
        <td style="padding:10px 16px;text-align:center;border-right:1px solid #E0D8DC">
          <div style="font-size:10px;text-transform:uppercase;letter-spacing:1.5px;color:#7A3A6A;font-weight:700">Trip Type</div>
          <div style="font-size:14px;font-weight:700;color:#2E2830;margin-top:2px">${d.tripType || 'Not specified'}</div>
        </td>
        <td style="padding:10px 16px;text-align:center;border-right:1px solid #E0D8DC">
          <div style="font-size:10px;text-transform:uppercase;letter-spacing:1.5px;color:#7A3A6A;font-weight:700">Group Size</div>
          <div style="font-size:14px;font-weight:700;color:#2E2830;margin-top:2px">${d.groupSize || '?'}</div>
        </td>
        <td style="padding:10px 16px;text-align:center;border-right:1px solid #E0D8DC">
          <div style="font-size:10px;text-transform:uppercase;letter-spacing:1.5px;color:#7A3A6A;font-weight:700">Budget / Person</div>
          <div style="font-size:14px;font-weight:700;color:#2E2830;margin-top:2px">${d.budgetPerPerson || '?'}</div>
        </td>
        <td style="padding:10px 16px;text-align:center">
          <div style="font-size:10px;text-transform:uppercase;letter-spacing:1.5px;color:#7A3A6A;font-weight:700">Deadline</div>
          <div style="font-size:14px;font-weight:700;color:#2E2830;margin-top:2px">${d.itineraryDeadline || '?'}</div>
        </td>
      </tr>
    </table>

    <!-- Reply CTA -->
    <div style="padding:12px 24px;background:#EEF2EA;text-align:center">
      <a href="mailto:${d.email}?subject=Your Mapped by Mattie Itinerary"
         style="display:inline-block;background:#4A5A40;color:#ffffff;font-size:13px;font-weight:700;
                padding:10px 24px;border-radius:6px;text-decoration:none;letter-spacing:.5px">
        Reply to ${d.name || 'Client'} &rarr;
      </a>
    </div>

    <!-- Full answers table -->
    <table width="100%" cellpadding="0" cellspacing="0"
           style="border-collapse:collapse;border:1px solid #E0D8DC;border-top:none">
      ${tableBody}
    </table>

    <!-- Footer -->
    <div style="background:#2E2830;padding:16px 24px;text-align:center">
      <p style="margin:0;color:rgba(250,247,245,.4);font-size:11px">
        Mapped by Mattie &bull; Plan Bold. Travel Better. &bull; @MappedByMattie
      </p>
    </div>
  </div>
</body>
</html>`;
}

// ── Public submission function ────────────────────────────────────────────────

export async function submitSurveyEmail(data: SurveyData): Promise<void> {
  const html = buildEmailHtml(data);

  const payload = {
    access_key: WEB3FORMS_ACCESS_KEY,
    subject: `New MbM Survey: ${data.name || 'Anonymous'} — ${data.tripType || 'Trip'} (${data.groupSize || '?'} travelers, ${data.budgetPerPerson || '?'}/person)`,
    from_name: 'Mapped by Mattie Survey',
    to_email: RECIPIENT_EMAIL,
    html,
    // Plain-text fallback
    message: `New survey from ${data.name} (${data.email}). Trip: ${data.tripType}. Dates: ${data.preferredDates}. Duration: ${data.duration}. Budget: ${data.budgetPerPerson}/person. Group: ${data.groupSize}. Deadline: ${data.itineraryDeadline}.`,
  };

  const response = await axios.post(
    'https://api.web3forms.com/submit',
    payload,
    {
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      timeout: 15000,
    }
  );

  if (!response.data?.success) {
    throw new Error(response.data?.message || 'Submission failed');
  }
}
